import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApexOptions, ChartComponent } from 'ng-apexcharts';
import { AttendanceService } from 'src/shared/services/attendance.service';
import { AuthService } from 'src/shared/services/auth.service';
import { ResponseService } from 'src/shared/services/response-service.service';
import { SchoolProgramService } from 'src/shared/services/school-program.service';
import { TranslateService } from 'src/shared/services/translate.service';
import Swal from 'sweetalert2';

// export type ChartOptions = {
//   series: ApexNonAxisChartSeries;
//   chart: ApexChart;
//   responsive: ApexResponsive[];
//   labels: any;
//   title: ApexTitleSubtitle;
// };

@Component({
  selector: 'app-attendance',
  templateUrl: './attendance.component.html',
  styleUrl: './attendance.component.scss'
})
export class AttendanceComponent implements OnInit {

  
  paramForm: FormGroup;
  dataSession: any[] = [];
  loading = false;
  dataAttendance: any[] = [];
  columnsToDisplay = ['session_date', 'status', 'present', 'absent', 'rate'];

  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ApexOptions> | any = {};

  attendanceCreated: boolean = false;

  activityName = '';
  summary = { present: 0, absent: 0, rate: null as number | null, recorded: 0, total: 0 };
  chartSessionCount = 0;
  today = new Date();

  supervisorLocationAccess: any;

  subscription: any = {};

  constructor(
    private attendanceService: AttendanceService,
    private spService: SchoolProgramService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private rpSer: ResponseService,
    public auth: AuthService
  ){}

  ngOnInit(): void {
    this.auth.loadPermissions(true);
    if (this.auth.permissions['SCHOOL_PROGRAM_ATTENDANCE_CREATE'] || this.auth.permissions['SCHOOL_PROGRAM_SESSION_EDIT']) {
      this.columnsToDisplay.push('action');
    }
    this.translate.load('extracurricular', 'attendance');
    this.initForm();
  }
  
  initForm() {
    this.paramForm = new FormGroup({
      'academic_year': new FormControl(null),
      'period_id': new FormControl(null),
      'school_location_id': new FormControl(null),
      'school_level_id': new FormControl(null),
      'school_program_id': new FormControl(null),
      'school_program_category_id': new FormControl(null),
      'school_program_activity_id': new FormControl(null),
      'year_level_id': new FormControl(null)
    });
  }

  onChangeAcademicYear(item: any) {
    this.paramForm.controls['academic_year'].markAsTouched();
    this.paramForm.controls['academic_year'].setValue(item ? item.academic_year : null);
    this.paramForm.controls['period_id'].markAsTouched();
    this.paramForm.controls['period_id'].setValue(item ? item.period_id : null);

    this.paramForm.controls['school_location_id'].setValue(null);
    this.paramForm.controls['school_level_id'].setValue(null);
    this.paramForm.controls['year_level_id'].setValue(null);
    this.paramForm.controls['school_program_id'].setValue(null);
    this.paramForm.controls['school_program_category_id'].setValue(null);
    this.paramForm.controls['school_program_activity_id'].setValue(null);

    if (item) {
      this.getSupervisorLocationAccess();
    }
  }

  onChangeSchoolLevel(item: any) {
    this.paramForm.controls['school_location_id'].markAsTouched();
    this.paramForm.controls['school_location_id'].setValue(item ? item.school_location_id : null);

    this.paramForm.controls['school_level_id'].markAsTouched();
    this.paramForm.controls['school_level_id'].setValue(item ? item.school_level_id : null);

    this.paramForm.controls['year_level_id'].setValue(null);

    this.paramForm.controls['school_program_id'].setValue(null);
    this.paramForm.controls['school_program_category_id'].setValue(null);
    this.paramForm.controls['school_program_activity_id'].setValue(null);
  }

  onChangeSchoolProgram(item: any) {
    this.paramForm.controls['school_program_id'].markAsTouched();
    this.paramForm.controls['school_program_id'].setValue(item ? item.school_program_id : null);

    this.paramForm.controls['school_program_category_id'].setValue(null);
    this.paramForm.controls['school_program_activity_id'].setValue(null);
  }

  onChangeSchoolProgramCategory(item: any) {
    this.paramForm.controls['school_program_category_id'].markAsTouched();
    this.paramForm.controls['school_program_category_id'].setValue(item ? item.school_program_category_id : null);

    this.paramForm.controls['school_program_activity_id'].setValue(null);
  }

  onChangeSchoolProgramActivity(item: any) {
    this.paramForm.controls['school_program_activity_id'].markAsTouched();
    this.paramForm.controls['school_program_activity_id'].setValue(item ? item.school_program_activity_id : null);
    this.activityName = item?.activity_name || '';
    if (item) {{
      this.getSessionAttendance();
      // read by the attendance list's Excel template download
      this.attendanceService.activityName = item.activity_name;
    }}
  }

  clearData() {
    this.dataSession = [];
    this.chartSessionCount = 0;
  }

  getSessionRate(session: any): number | null {
    const total = (+session.present_count || 0) + (+session.absent_count || 0);
    return session.attendance_created && total > 0 ? Math.round((+session.present_count / total) * 100) : null;
  }

  getSessionState(session: any): { label: string; tone: string } {
    if (session.disabled) {
      return { label: 'Disabled', tone: 'muted' };
    }
    if (session.eventday) {
      return { label: 'Event Day', tone: 'event' };
    }
    if (session.attendance_created) {
      return { label: 'Recorded', tone: 'done' };
    }
    return new Date(session.activity_session_date) > this.today
      ? { label: 'Upcoming', tone: 'upcoming' }
      : { label: 'Not recorded', tone: 'todo' };
  }

  // Totals, overall rate and the per-session rate chart. Only recorded sessions count toward the rate.
  private buildSummary(meta: any): void {
    const recorded = this.dataSession.filter((session: any) => this.getSessionRate(session) !== null);
    const present = +meta?.total_present || 0;
    const absent = +meta?.total_absent || 0;
    const rate = present + absent > 0 ? Math.round((present / (present + absent)) * 100) : null;

    this.summary = {
      present,
      absent,
      rate,
      recorded: this.dataSession.filter((session: any) => session.attendance_created).length,
      total: this.dataSession.filter((session: any) => !session.disabled && !session.eventday).length,
    };
    this.chartSessionCount = recorded.length;

    const points = recorded.map((session: any) => ({
      x: formatDate(session.activity_session_date, 'd MMM', 'en-US'),
      y: this.getSessionRate(session),
      date: formatDate(session.activity_session_date, 'EEE, d MMM y', 'en-US'),
      present: +session.present_count || 0,
      absent: +session.absent_count || 0,
    }));
    const ink = '#1c2233';
    const muted = '#3d4555';

    this.chartOptions = {
      series: [{ name: 'Attendance rate', data: points }],
      chart: {
        type: 'bar',
        height: 240,
        fontFamily: "'DM Sans', sans-serif",
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
      },
      colors: ['#2f6fed'],
      plotOptions: {
        bar: {
          // Thin columns: cap at 24px, fall back to a share of the slot when sessions are many
          columnWidth: points.length <= 12 ? '24px' : '60%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
        },
      },
      dataLabels: { enabled: false },
      states: { hover: { filter: { type: 'darken', value: 0.85 } } },
      grid: {
        borderColor: '#e7eaf2',
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        padding: { left: 4, right: 8 },
      },
      xaxis: {
        type: 'category',
        axisBorder: { color: '#d5dbe7' },
        axisTicks: { show: false },
        labels: { rotate: -45, hideOverlappingLabels: true, style: { colors: muted, fontSize: '12px' } },
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 4,
        labels: { formatter: (value: number) => `${Math.round(value)}%`, style: { colors: muted, fontSize: '12px' } },
      },
      annotations: rate === null ? {} : {
        yaxis: [{
          y: rate,
          borderColor: ink,
          strokeDashArray: 0,
          opacity: 0.6,
          label: {
            text: `Average ${rate}%`,
            position: 'left',
            textAnchor: 'start',
            offsetX: 4,
            borderWidth: 0,
            style: { background: '#ffffff', color: ink, fontSize: '12px', fontWeight: 600 },
          },
        }],
      },
      tooltip: {
        custom: ({ dataPointIndex }: any) => {
          const point = points[dataPointIndex];
          return `<div class="att-tip">
              <div class="att-tip__date">${point.date}</div>
              <div class="att-tip__rate">${point.y}% attended</div>
              <div class="att-tip__counts">${point.present} present · ${point.absent} absent</div>
            </div>`;
        },
      },
    };
  }

  getSupervisorLocationAccess() {
    if (this.subscription.get_supervisor_location_access) {
      this.subscription.get_supervisor_location_access.unsubscribe();
    }
    this.subscription.get_supervisor_location_access = this.spService.getSupervisorLocationAccess({
      academic_year: this.paramForm.controls['academic_year'].value,
    }).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.supervisorLocationAccess = res.data;
        } else {
          this.rpSer.showAddError(res.message);
        }
      },
      error: (err: any) => {
        this.rpSer.showAddError();
      }
    });
  }

  getSessionAttendance() {
    this.dataSession = [];
    this.loading = true;
    const params = {
      school_program_activity_id: this.paramForm.get('school_program_activity_id')?.value,
      academic_year: this.paramForm.get('academic_year')?.value,
      school_level_id: this.paramForm.get('school_level_id')?.value,
      period_id: this.paramForm.get('period_id')?.value,
      year_level_id: this.paramForm.get('year_level_id')?.value,
      school_location_id: this.paramForm.get('school_location_id')?.value,
    }
    this.attendanceService.getActivitySessionList(params).subscribe({
      next: (response: any) => {
        if (response.status) {
          this.dataSession = response.data;
          this.buildSummary(response.meta);
          this.loading = false;
        } else {
          this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
          this.loading = false;
        }
      },
      error: (error: any) => {
        this.rpSer.showAddError();
        this.loading = false;
      }
    });
  }

  // Router state gives the list page a title without another request
  private openAttendanceList(sessionId: number, sessionDate?: any): void {
    this.router.navigate(['./attendance-list/' + sessionId + '/' + this.paramForm.get('period_id')?.value], {
      relativeTo: this.route,
      state: { activityName: this.activityName, sessionDate },
    });
  }

  onEntryAttendance(sessionId: number, sessionDate?: any) {
    Swal.fire({
      title: 'Confirmation',
      text: 'Are you sure you want to create attendance for this activity session?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then((result) => {
      if (result.isConfirmed) {
        const params = {
          activity_session_id: sessionId,
          school_program_activity_id: this.paramForm.get('school_program_activity_id')?.value,
          period_id: this.paramForm.get('period_id')?.value,
        }
        this.attendanceService.entrySessionAttendance(params).subscribe({
          next: (response: any) => {
            if (response.status) {
              this.dataAttendance = response.data;
              // this.getSessionAttendance();
              this.openAttendanceList(sessionId, sessionDate);
            } else {
              this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
            }
          },
          error: (error: any) => {
            this.rpSer.showAddError();
          }
        });
      }
    });
  }

  onViewAttendance(sessionId: number, sessionDate?: any) {
    this.openAttendanceList(sessionId, sessionDate);
  }

  onToggleDisableAttendance(sessionId: number, isDisabled: boolean) {
    Swal.fire({
      title: 'Confirmation',
      text: isDisabled ? 'Are you sure you want to disable this session?' : 'Are you sure you want to enable this session?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then((result) => {
      if (result.isConfirmed) {
        const params = {
          activity_session_id: sessionId,
          is_disabled: isDisabled,
        }
        this.attendanceService.toggleDisableSession(params).subscribe({
          next: (response: any) => {
            if (response.status) {
              this.getSessionAttendance();
            } else {
              this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
            }
          },
          error: (error: any) => {
            this.rpSer.showAddError();
          }
        });
      }
    });
  }

}
